import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Heart, MessageCircle, X, Send, Info, Loader2, Coins } from 'lucide-react';
import { useAppState } from '../../context/AppStateContext';
import { useAuth } from '../../context/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { NICHE_IMAGES, GROUPS_LIST } from '../../utils/constants';
import { hasDifferentCurrency, convertToLocalCurrency } from '../../services/exchangeRateService';
import { getCountryCode } from '../../utils/currency';
import { getCommentAvatar } from '../../utils/avatar';
import DealInfoDrawer from '../shop/DealInfoDrawer';
import GroupChatDrawer from '../groups/GroupChatDrawer';
import GroupDealsDrawer from '../groups/GroupDealsDrawer';
import { AnimatedNumber } from '../ui/AnimatedNumber';

export default function HomeTab() {
  const navigate = useNavigate();
  const {
    feedData,
    feedLoading,
    feedError,
    toggleLike,
    setShowNotifications,
    unreadCount,
    profileForm,
    isolatedGroupDeals,
    setSelectedDeal,
    setShowDealInfoDrawer,
    fetchAndSelectGroup,
    getFeedComments,
    addFeedComment,
    fetchFeedComments,
    joinedGroupIds,
  } = useAppState();
  const { user, rewardsData, isGuestMode, silentRefreshBalance } = useAuth();

  const { isDesktop } = useResponsive();
  const scrollContainerRef = useRef(null);
  const [showCommentsDrawer, setShowCommentsDrawer] = useState(false);
  const [selectedDealForComments, setSelectedDealForComments] = useState(null);
  const [showFeedCommentsDrawer, setShowFeedCommentsDrawer] = useState(false);
  const [selectedFeedItem, setSelectedFeedItem] = useState(null);
  const [showStoryInfoDrawer, setShowStoryInfoDrawer] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const [localCurrency, setLocalCurrency] = useState(null);

  // Pre-compute group membership for deal items when feed loads
  // This creates a Set of groupIds from deals where the user is already a member
  const memberDealGroupIds = useMemo(() => {
    if (!feedData || feedData.length === 0) return new Set();

    // Convert joinedGroupIds to Set for O(1) lookups
    const joinedSet = new Set(joinedGroupIds);

    // Get groupIds from deal-type feed items where user is a member
    const memberIds = feedData
      .filter((item) => item.type === 'deal' && (item.groupId || item.group))
      .map((item) => item.groupId || item.group)
      .filter((groupId) => joinedSet.has(groupId));

    console.log('Pre-computed member deal groups:', memberIds.length, 'of', feedData.filter(i => i.type === 'deal').length, 'deals');

    return new Set(memberIds);
  }, [feedData, joinedGroupIds]);

  // Helper function to check if user is a member of a deal's group
  const isUserMemberOfDealGroup = (feedItem) => {
    const groupId = feedItem?.groupId || feedItem?.group;
    if (!groupId) return false;
    return memberDealGroupIds.has(groupId);
  };

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const userFirstName = user?.first_name || profileForm.firstName || 'there';

  // Scroll to top on mount (with delay to ensure content is rendered)
  useEffect(() => {
    // Immediate scroll
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
    // Also scroll after a short delay to handle navigation transitions
    const timeoutId = setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  // Silently refresh balance in background when home screen loads
  // Shows cached value immediately, then updates seamlessly when fresh data arrives
  useEffect(() => {
    if (!isGuestMode) {
      silentRefreshBalance();
    }
  }, [isGuestMode, silentRefreshBalance]);

  // Check if profile is incomplete (any required field is empty)
  const isProfileIncomplete = !user?.first_name || !user?.last_name || (!user?.email && !user?.phone && !user?.phone_number);

  // Get current balance from API data
  const currentBalance = rewardsData?.balance || user?.available_bold_credits || user?.bold_credits || 0;

  // Get local currency if different from USD (uses country from profile or user data)
  // Convert country name to code (API returns "NIGERIA", we need "NG")
  const userCountryCode = getCountryCode(user?.country) || profileForm.country;
  const showLocalCurrency = hasDifferentCurrency(userCountryCode);

  // Fetch exchange rate and convert currency (convert points to USD first, then to local)
  useEffect(() => {
    if (showLocalCurrency && currentBalance > 0) {
      const usdAmount = currentBalance / 1000; // Convert points to USD
      convertToLocalCurrency(usdAmount, userCountryCode)
        .then(setLocalCurrency)
        .catch(() => setLocalCurrency(null));
    } else {
      setLocalCurrency(null);
    }
  }, [showLocalCurrency, currentBalance, userCountryCode]);

  // Format balance for display
  const formatBalance = (balance) => {
    if (typeof balance !== 'number' || isNaN(balance)) return '0.00';
    return balance.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Format points for display (API returns points directly as available_bold_credits)
  const formatPoints = (balance) => {
    if (typeof balance !== 'number' || isNaN(balance)) return '0';
    // Balance from API is already in points
    return Math.floor(balance).toLocaleString('en-US');
  };

  // Convert points to USD (1000 points = $1 USD)
  const pointsToUSD = (points) => {
    if (typeof points !== 'number' || isNaN(points)) return '0.00';
    const usd = points / 1000;
    return usd.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Find deal by feed item (fallback for additional deal info)
  const findDealByFeedItem = (feedItem) => {
    const allDeals = Object.values(isolatedGroupDeals).flat();
    return allDeals.find((d) => d.brand === feedItem.author || d.groupId === feedItem.groupId);
  };

  // Handle deal badge click - use groupId from feed item directly
  // Opens group thread if member, group preview if not
  const handleDealBadgeClick = async (feedItem) => {
    const groupId = feedItem.groupId || feedItem.group;

    if (!groupId) {
      console.warn('No groupId found in feed item:', feedItem);
      return;
    }

    // Use pre-computed membership check for faster routing
    if (isUserMemberOfDealGroup(feedItem)) {
      // User is a member - fetch group from API and open group thread/chat
      console.log('User is member of group, opening group thread:', groupId);
      await fetchAndSelectGroup(groupId);
    } else {
      // User is NOT a member - open group preview drawer
      // Try to find existing deal for full info, or create minimal deal object from feed item
      const existingDeal = findDealByFeedItem(feedItem);

      if (existingDeal) {
        setSelectedDeal(existingDeal);
      } else {
        // Create a deal-like object from feed item for the preview
        setSelectedDeal({
          id: feedItem.id,
          groupId: groupId,
          groupName: feedItem.groupName || feedItem.group,
          brand: feedItem.author || feedItem.brand,
          offer: feedItem.dealBadge || 'Exclusive Deal',
          logo: (feedItem.author || feedItem.brand || 'D').charAt(0).toUpperCase(),
          color: 'bg-indigo-600',
          terms: feedItem.content || 'Join the group to unlock this exclusive deal and coordinate with other members.',
          image: feedItem.image,
        });
      }
      setShowDealInfoDrawer(true);
    }
  };

  // Handle comment click - show comments drawer if joined, else open deal info
  const handleCommentClick = (feedItem) => {
    if (feedItem.type === 'deal') {
      const groupId = feedItem.groupId || feedItem.group;

      if (groupId && isUserMemberOfDealGroup(feedItem)) {
        // User is in this group - show comments drawer
        const existingDeal = findDealByFeedItem(feedItem);
        const dealForComments = existingDeal || {
          id: feedItem.id,
          groupId: groupId,
          brand: feedItem.author || feedItem.brand,
        };
        setSelectedDealForComments(dealForComments);
        setShowCommentsDrawer(true);
      } else {
        // User not in group - open deal info drawer (triggers join flow)
        handleDealBadgeClick(feedItem);
      }
    } else {
      // Story, insight, or tip - show feed comments drawer
      setSelectedFeedItem(feedItem);
      setShowFeedCommentsDrawer(true);
      // Fetch comments from API in background
      fetchFeedComments(feedItem.id);
    }
  };

  // Handle story/post title or content click - opens story info drawer
  const handleStoryClick = (feedItem) => {
    // Only open story drawer for non-deal items (stories, insights, tips)
    if (feedItem.type !== 'deal') {
      setSelectedStory(feedItem);
      setShowStoryInfoDrawer(true);
      // Fetch comments from API in background
      fetchFeedComments(feedItem.id);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Header - Mobile only (Desktop uses GlobalHeader) */}
        {!isDesktop && (
          <header className="sticky top-0 bg-white z-10 px-4 pb-3 flex items-center justify-between border-b border-gray-100 pt-[calc(0.75rem+env(safe-area-inset-top))]">
            <button
              onClick={() => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-3"
            >
              <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="h-9 w-9" />
              <h1 className="font-bold text-gray-900 text-lg">
                {getGreeting()}, {userFirstName}
              </h1>
            </button>
            <button
              onClick={() => setShowNotifications(true)}
              className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <Bell size={22} />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-green-500 rounded-full" />
              )}
            </button>
          </header>
        )}
        <div className={`px-4 py-6 ${isDesktop ? 'max-w-4xl mx-auto' : ''}`}>

        {/* Rewards Card */}
        <div className="hero-card bg-gradient-card rounded-2xl p-6 text-white mb-6 relative overflow-hidden">
          {/* Background coin icons */}
          <Coins size={80} className="absolute -right-4 -top-4 text-white/10 rotate-12" />
          <Coins size={60} className="absolute right-16 bottom-2 text-white/10 -rotate-12" />
          <Coins size={40} className="absolute left-1/2 -top-2 text-white/10 rotate-45" />

          {isGuestMode ? (
            <div className="relative z-10">
              <p className="text-lg font-bold mb-2">Create your account to start enjoying group rewards</p>
              <button
                onClick={() => navigate('/signup')}
                className="mt-2 px-6 py-2.5 bg-white text-indigo-600 font-bold rounded-xl hover:bg-white/90 transition-colors"
              >
                Get Started
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-white/70 font-medium mb-1 relative z-10">Your Reward Balance</p>
              {/* Only show loading if no cached balance exists */}
              {rewardsData?.isLoading && currentBalance === 0 ? (
                <div className="flex items-center gap-2 py-2 relative z-10">
                  <Loader2 size={20} className="animate-spin text-white/70" />
                  <span className="text-white/70">Loading...</span>
                </div>
              ) : (
                <div className="relative z-10">
                  <div className="flex items-baseline gap-1 mb-1">
                    {/* AnimatedNumber for smooth balance updates */}
                    <AnimatedNumber
                      value={currentBalance}
                      formatter={formatPoints}
                      duration={1200}
                      className="text-2xl font-bold"
                    />
                    <sup className="text-xs text-white/70 font-medium">PTS</sup>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-base">USD {pointsToUSD(currentBalance)}</span>
                    {localCurrency && (
                      <>
                        <span className="text-white/40">|</span>
                        <span className="text-base">{localCurrency.formatted}</span>
                      </>
                    )}
                  </div>
                </div>
              )}
              {isProfileIncomplete && (
                <button
                  onClick={() => navigate('/profile')}
                  className="mt-4 w-full flex items-center gap-2 bg-white/15 hover:bg-white/20 rounded-xl px-4 py-3 transition-colors relative z-10"
                >
                  <Info size={16} className="text-amber-300 flex-shrink-0" />
                  <span className="text-sm text-white/90">Complete your profile to unlock your rewards →</span>
                </button>
              )}
            </>
          )}
        </div>

        {/* Feed */}
        {feedLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 size={32} className="animate-spin text-indigo-500 mb-3" />
            <p className="text-gray-500 text-sm">Loading feed...</p>
          </div>
        ) : feedError ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-red-500 text-sm mb-2">{feedError}</p>
            <p className="text-gray-400 text-xs">Showing cached content</p>
          </div>
        ) : feedData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-gray-500 text-sm">No feed items yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {feedData.map((item) => (
              <FeedCard
                key={item.id}
                item={item}
                onLike={() => toggleLike(item.id)}
                onDealBadgeClick={() => handleDealBadgeClick(item)}
                onCommentClick={() => handleCommentClick(item)}
                onStoryClick={() => handleStoryClick(item)}
                isJoinedGroup={item.type === 'deal' && joinedGroupIds.includes(item.groupId || item.group)}
              />
            ))}
          </div>
        )}
      </div>
    </div>

      {/* Deal Info Drawer */}
      <DealInfoDrawer />

      {/* Group Chat Drawer - opens on home page without navigating */}
      <GroupChatDrawer />
      <GroupDealsDrawer />

      {/* Deal Comments Drawer */}
      {showCommentsDrawer && selectedDealForComments && (
        <DealCommentsDrawer
          deal={selectedDealForComments}
          onClose={() => {
            setShowCommentsDrawer(false);
            setSelectedDealForComments(null);
          }}
        />
      )}

      {/* Feed Comments Drawer - for stories, insights, tips */}
      {showFeedCommentsDrawer && selectedFeedItem && (
        <FeedCommentsDrawer
          item={selectedFeedItem}
          comments={getFeedComments(selectedFeedItem.id)}
          onAddComment={(text) => addFeedComment(selectedFeedItem.id, {
            author: user?.first_name || 'You',
            avatar: getCommentAvatar(profileForm?.gender, user?.first_name || 'You'),
            text,
          })}
          onClose={() => {
            setShowFeedCommentsDrawer(false);
            setSelectedFeedItem(null);
          }}
        />
      )}

      {/* Story Info Drawer - full story view with inline comments */}
      {showStoryInfoDrawer && selectedStory && (() => {
        // Get the current item from feedData to reflect latest like state
        const currentStory = feedData.find(item => item.id === selectedStory.id) || selectedStory;
        return (
          <StoryInfoDrawer
            item={currentStory}
            comments={getFeedComments(currentStory.id)}
            onAddComment={(text) => addFeedComment(currentStory.id, {
              author: user?.first_name || 'You',
              avatar: getCommentAvatar(profileForm?.gender, user?.first_name || 'You'),
              text,
            })}
            onClose={() => {
              setShowStoryInfoDrawer(false);
              setSelectedStory(null);
            }}
            onLike={() => toggleLike(currentStory.id)}
          />
        );
      })()}
    </div>
  );
}

function FeedCard({ item, onLike, onDealBadgeClick, onCommentClick, onStoryClick, isJoinedGroup }) {
  // Format date to readable format (e.g., "Jan 1, 2026")
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // If already in readable format, return as-is
    if (!/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr;
    // Parse ISO date and format
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Strip HTML tags from content for preview
  const stripHtml = (html) => {
    if (!html) return '';
    // Remove HTML tags and decode entities
    const text = html
      .replace(/<br\s*\/?>/gi, ' ') // Replace <br> with space
      .replace(/<\/p>\s*<p>/gi, ' ') // Replace paragraph breaks with space
      .replace(/<[^>]+>/g, '') // Remove all other HTML tags
      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
      .replace(/&amp;/g, '&') // Decode &amp;
      .replace(/&lt;/g, '<') // Decode &lt;
      .replace(/&gt;/g, '>') // Decode &gt;
      .replace(/&quot;/g, '"') // Decode &quot;
      .replace(/&#39;/g, "'") // Decode &#39;
      .replace(/\s+/g, ' ') // Collapse multiple spaces
      .trim();
    return text;
  };

  // Get plain text content for preview
  const contentPreview = stripHtml(item.content);

  // Check if this is a clickable story (non-deal items)
  const isClickableStory = item.type !== 'deal';

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
      {/* Author Header */}
      <div className="flex items-center gap-3 p-4">
        <img
          src={item.avatar}
          alt={item.author}
          className="w-10 h-10 rounded-full bg-gray-100"
        />
        <div className="flex-1">
          <p className="font-semibold text-gray-900">{item.author}</p>
          <p className="text-xs text-gray-400">{formatDate(item.date) || item.time}</p>
        </div>
      </div>

      {/* Image */}
      {item.image && (
        <div className="relative aspect-video">
          <img
            src={item.image}
            alt={item.title}
            className="w-full h-full object-cover"
          />
          {item.type === 'deal' && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          )}
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
        <p className="text-sm text-gray-600 line-clamp-2">{contentPreview}</p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between px-4 pb-4">
        <div className="flex items-center gap-6">
          <button
            onClick={onLike}
            className={`flex items-center gap-1.5 transition-colors ${
              item.isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
            }`}
          >
            <Heart size={20} fill={item.isLiked ? 'currentColor' : 'none'} />
            <span className="text-sm font-medium">{item.likes}</span>
          </button>
          <button
            onClick={onCommentClick}
            className={`flex items-center gap-1.5 transition-colors ${
              (item.type === 'deal' && isJoinedGroup) || item.type !== 'deal'
                ? 'text-indigo-500 hover:text-indigo-600'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <MessageCircle size={20} />
            <span className="text-sm font-medium">{item.comments || 'Comment'}</span>
          </button>
        </div>
        {/* Deal Badge - Bottom Right */}
        {item.type === 'deal' && item.dealBadge && (
          <button
            onClick={onDealBadgeClick}
            className="px-3 py-1.5 bg-indigo-600 text-white text-[10px] font-black uppercase rounded-xl shadow-sm hover:bg-indigo-700 active:scale-95 transition-all"
          >
            {item.dealBadge}
          </button>
        )}
        {/* Read More Badge - for stories, insights, tips */}
        {item.type !== 'deal' && (
          <button
            onClick={onStoryClick}
            className="px-3 py-1.5 bg-gray-100 text-gray-700 text-[10px] font-black uppercase rounded-xl shadow-sm hover:bg-gray-200 active:scale-95 transition-all"
          >
            Read More
          </button>
        )}
      </div>
    </div>
  );
}

// Deal Comments Drawer Component
function DealCommentsDrawer({ deal, onClose }) {
  const { getDealComments, addGroupMessage, profileForm } = useAppState();
  const { user, isGuestMode, setShowGuestPrompt } = useAuth();
  const [newComment, setNewComment] = useState('');

  // Lock body scroll when drawer is open
  useBodyScrollLock(true);

  const group = GROUPS_LIST.find((g) => g.id === deal.groupId);
  const comments = getDealComments(deal.groupId, deal.id);

  const handleSubmitComment = () => {
    if (!newComment.trim()) return;

    // Guest users need to sign up to comment
    if (isGuestMode) {
      setShowGuestPrompt(true);
      return;
    }

    // Add comment to shared state - syncs to group chat
    addGroupMessage(deal.groupId, {
      type: 'deal_comment',
      dealId: deal.id,
      dealBrand: deal.brand,
      author: user?.first_name || 'You',
      avatar: getCommentAvatar(profileForm?.gender, user?.first_name || 'You'),
      text: newComment,
    });
    setNewComment('');
  };

  return (
    <>
      {/* Backdrop */}
      <div className="backdrop animate-fade-in" onClick={onClose} />

      {/* Full Screen Panel */}
      <div className="slide-panel">
        <div>
          {/* Header - Fixed */}
          <div className="slide-panel-header flex items-center justify-between p-4 border-b border-gray-100">
            <div>
              <h2 className="font-bold text-lg text-gray-900">{deal.brand} Thread</h2>
              <p className="text-sm text-gray-500">on #{group?.name} • {comments.length} {comments.length === 1 ? 'comment' : 'comments'}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          {/* Comments List - Scrollable */}
          <div className="slide-panel-content p-4 space-y-4 bg-gray-50">
            {comments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No comments yet</p>
                <p className="text-gray-400 text-xs mt-1">Be the first to comment!</p>
              </div>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="flex gap-3">
                  <img
                    src={comment.avatar}
                    alt={comment.author}
                    className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">{comment.author}</span>
                      <span className="text-xs text-gray-400">{comment.time}</span>
                    </div>
                    <p className="text-sm text-gray-700">{comment.text}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Input - Fixed at bottom */}
          <div className="slide-panel-footer p-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmitComment()}
                placeholder="Add a comment..."
                className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={handleSubmitComment}
                disabled={!newComment.trim()}
                className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Feed Comments Drawer - for stories, insights, tips
function FeedCommentsDrawer({ item, comments, onAddComment, onClose }) {
  const { isGuestMode, setShowGuestPrompt } = useAuth();
  const [newComment, setNewComment] = useState('');

  // Lock body scroll when drawer is open
  useBodyScrollLock(true);

  // Strip HTML tags from content for preview
  const stripHtml = (html) => {
    if (!html) return '';
    return html
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>\s*<p>/gi, ' ')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim();
  };

  const contentPreview = stripHtml(item.content);

  const handleSubmitComment = () => {
    if (!newComment.trim()) return;

    // Guest users need to sign up to comment
    if (isGuestMode) {
      setShowGuestPrompt(true);
      return;
    }

    onAddComment(newComment);
    setNewComment('');
  };

  const getTypeLabel = () => {
    switch (item.type) {
      case 'story': return 'Story';
      case 'insight': return 'Insight';
      default: return 'Post';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="backdrop animate-fade-in" onClick={onClose} />

      {/* Full Screen Panel */}
      <div className="slide-panel">
        <div>
          {/* Header - Fixed */}
          <div className="slide-panel-header flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <img
                src={item.avatar}
                alt={item.author}
                className="w-10 h-10 rounded-full bg-gray-100"
              />
              <div>
                <h2 className="font-bold text-lg text-gray-900">{item.author}</h2>
                <p className="text-sm text-gray-500">{getTypeLabel()} • {comments.length} comments</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          {/* Original Post Preview - Part of header area */}
          <div className="slide-panel-header p-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
            <p className="text-sm text-gray-600 line-clamp-2">{contentPreview}</p>
          </div>

          {/* Comments List - Scrollable */}
          <div className="slide-panel-content p-4 space-y-4 bg-gray-50">
            {comments.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 text-sm">No comments yet</p>
                <p className="text-gray-400 text-xs mt-1">Be the first to share your thoughts!</p>
              </div>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="flex gap-3">
                  <img
                    src={comment.avatar}
                    alt={comment.author}
                    className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">{comment.author}</span>
                      <span className="text-xs text-gray-400">{comment.time}</span>
                    </div>
                    <p className="text-sm text-gray-700">{comment.text}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Input - Fixed at bottom */}
          <div className="slide-panel-footer p-4 border-t border-gray-100">
            {isGuestMode ? (
              <button
                onClick={() => setShowGuestPrompt(true)}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold text-sm uppercase tracking-wide shadow-lg hover:bg-indigo-700 active:scale-[0.98] transition-all"
              >
                Create Account to Chat
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmitComment()}
                  placeholder="Add a comment..."
                  className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim()}
                  className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={20} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// Story Info Drawer - Full story view with proper formatting and inline comments
function StoryInfoDrawer({ item, comments = [], onAddComment, onClose, onLike }) {
  const { isGuestMode, setShowGuestPrompt } = useAuth();
  const [newComment, setNewComment] = useState('');

  // Lock body scroll when drawer is open
  useBodyScrollLock(true);

  const handleSubmitComment = () => {
    if (!newComment.trim()) return;

    // Guest users need to sign up to comment
    if (isGuestMode) {
      setShowGuestPrompt(true);
      return;
    }

    onAddComment(newComment);
    setNewComment('');
  };

  // Format date to readable format
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (!/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Get type label with icon
  const getTypeLabel = () => {
    switch (item.type) {
      case 'story': return 'Story';
      case 'insight': return 'Insight';
      case 'tip': return 'Tip';
      default: return 'Post';
    }
  };

  // Check if content contains HTML tags
  const isHtmlContent = (content) => {
    if (!content) return false;
    return /<[^>]+>/.test(content);
  };

  // Check if the content is HTML
  const hasHtmlContent = isHtmlContent(item.content);

  // Format plain text content with proper paragraph spacing
  // Handles both newline-separated and period-separated content
  const formatContent = (content) => {
    if (!content) return [];

    // First check if content has explicit paragraph breaks (double newlines or \n\n)
    if (content.includes('\n\n')) {
      return content.split('\n\n').filter(p => p.trim());
    }

    // Check for single newlines
    if (content.includes('\n')) {
      return content.split('\n').filter(p => p.trim());
    }

    // If no newlines, try to split long content into logical paragraphs
    // by looking for sentence endings followed by capital letters (new paragraphs)
    const sentences = content.split(/(?<=[.!?])\s+(?=[A-Z])/);

    // Group sentences into paragraphs (approximately 2-3 sentences each for readability)
    const paragraphs = [];
    let currentParagraph = '';

    sentences.forEach((sentence, index) => {
      currentParagraph += (currentParagraph ? ' ' : '') + sentence;

      // Create a new paragraph after every 2-3 sentences or if content is very long
      if ((index + 1) % 3 === 0 || currentParagraph.length > 300) {
        paragraphs.push(currentParagraph);
        currentParagraph = '';
      }
    });

    // Don't forget the last paragraph
    if (currentParagraph) {
      paragraphs.push(currentParagraph);
    }

    return paragraphs.length > 0 ? paragraphs : [content];
  };

  const paragraphs = hasHtmlContent ? [] : formatContent(item.content);

  return (
    <>
      {/* Backdrop */}
      <div className="backdrop animate-fade-in" onClick={onClose} />

      {/* Full Screen Panel */}
      <div className="slide-panel">
        <div>
          {/* Header - Fixed */}
          <div className="slide-panel-header flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <img
                src={item.avatar}
                alt={item.author}
                className="w-10 h-10 rounded-full bg-gray-100"
              />
              <div>
                <h2 className="font-bold text-gray-900">{item.author}</h2>
                <p className="text-xs text-gray-500">
                  {getTypeLabel()} • {formatDate(item.date) || item.time}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
            >
              <X size={20} />
            </button>
          </div>

          {/* Story Content - Scrollable */}
          <div className="slide-panel-content p-0 bg-white">
            {/* Title and Content */}
            <div className="p-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-4 leading-tight">
                {item.title}
              </h1>

              {/* Story Content with proper paragraph formatting */}
              {hasHtmlContent ? (
                // Render HTML content with proper styling
                <div
                  className="story-html-content prose prose-gray max-w-none text-base text-gray-700 leading-relaxed
                    [&>p]:mb-4 [&>p:last-child]:mb-0
                    [&>p>br]:block [&>p>br]:h-2
                    [&_strong]:font-bold [&_strong]:text-gray-900
                    [&_em]:italic
                    [&_a]:text-indigo-600 [&_a]:underline
                    [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4
                    [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4
                    [&_li]:mb-1"
                  dangerouslySetInnerHTML={{ __html: item.content }}
                />
              ) : (
                // Render plain text with paragraph formatting
                <div className="space-y-4">
                  {paragraphs.map((paragraph, index) => (
                    <p key={index} className="text-base text-gray-700 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}

              {/* Tags or Category (if available) */}
              {item.tags && item.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t border-gray-100">
                  {item.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Engagement Actions */}
              <div className="flex items-center gap-6 py-4 mt-4 border-t border-gray-100">
                <button
                  onClick={onLike}
                  className={`flex items-center gap-2 transition-colors ${
                    item.isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                  }`}
                >
                  <Heart size={22} fill={item.isLiked ? 'currentColor' : 'none'} />
                  <span className="font-medium">{item.likes || 0}</span>
                </button>
                <div className="flex items-center gap-2 text-gray-500">
                  <MessageCircle size={22} />
                  <span className="font-medium">{comments.length} {comments.length === 1 ? 'comment' : 'comments'}</span>
                </div>
              </div>

              {/* Comments Section - Inline */}
              <div className="border-t border-gray-100 pt-4">
                <h3 className="font-bold text-gray-900 mb-4">Comments</h3>

                {comments.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-xl mb-4">
                    <MessageCircle size={32} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500 text-sm">No comments yet</p>
                    <p className="text-gray-400 text-xs mt-1">Be the first to share your thoughts!</p>
                  </div>
                ) : (
                  <div className="space-y-4 mb-4">
                    {comments.map((comment) => (
                      <div key={comment.id} className="flex gap-3">
                        <img
                          src={comment.avatar}
                          alt={comment.author}
                          className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm text-gray-900">{comment.author}</span>
                            <span className="text-xs text-gray-400">{comment.time}</span>
                          </div>
                          <p className="text-sm text-gray-700">{comment.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Comment Input - Fixed at bottom */}
          <div className="slide-panel-footer p-4 border-t border-gray-100 bg-white">
            {isGuestMode ? (
              <button
                onClick={() => setShowGuestPrompt(true)}
                className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold text-sm uppercase tracking-wide shadow-lg hover:bg-indigo-700 active:scale-[0.98] transition-all"
              >
                Create Account to Comment
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmitComment()}
                  placeholder="Add a comment..."
                  className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim()}
                  className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send size={20} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
